import React, { useState } from 'react';
import { auth } from '../FirebaseConfig';
import { API_BASE } from '../../apiSession';
import { relativeTime, exactTime } from '../../utils/formatTime';

// Copy, rate, regenerate — the row under a finished assistant reply.
//
// There was nothing here before. A user who wanted the text had to select it by
// hand across a scrolling panel, a bad answer could only be improved by
// retyping the question, and there was no way to tell us an answer was wrong.
//
// Every control does something real. The rating posts to
// /api/omni-agent/feedback and is stored; it is not a decorative thumbs-up
// that discards the click.

const Icon = ({ name }) => (
  <span className="material-symbols-rounded" aria-hidden="true">{name}</span>
);

const MessageActions = ({ message, sessionId, onRegenerate, canRegenerate }) => {
  const [copied, setCopied] = useState(false);
  const [rating, setRating] = useState(null);
  const [rateFailed, setRateFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content || '');
      setCopied(true);
      // Long enough to read, short enough that the button is available again
      // before someone wants it.
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // Clipboard access can be refused (insecure origin, permissions). Saying
      // nothing would look like the button is broken.
      console.error('[chat] copy failed', err);
      setRateFailed(false);
      window.prompt('Copy the reply:', message.content || '');
    }
  };

  const rate = async (value) => {
    // Optimistic: the click is acknowledged immediately, and reverted only if
    // the write actually fails. Clicking the same thumb again clears it.
    const next = rating === value ? null : value;
    setRating(next);
    setRateFailed(false);
    if (next === null) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE}/api/omni-agent/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messageId: message.id,
          sessionId,
          rating: next,
          content: message.content,
        }),
      });
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
    } catch (err) {
      console.error('[chat] could not record feedback', err);
      setRating(null);
      setRateFailed(true);
    }
  };

  return (
    <div className="ag-msg-actions">
      <button
        type="button"
        className={`ag-msg-action${copied ? ' is-done' : ''}`}
        onClick={copy}
        aria-label={copied ? 'Copied' : 'Copy reply'}
        title={copied ? 'Copied' : 'Copy'}
      >
        <Icon name={copied ? 'check' : 'content_copy'} />
      </button>

      <button
        type="button"
        className={`ag-msg-action${rating === 'up' ? ' is-on' : ''}`}
        onClick={() => rate('up')}
        aria-pressed={rating === 'up'}
        aria-label="Good response"
        title="Good response"
      >
        <Icon name="thumb_up" />
      </button>

      <button
        type="button"
        className={`ag-msg-action${rating === 'down' ? ' is-on' : ''}`}
        onClick={() => rate('down')}
        aria-pressed={rating === 'down'}
        aria-label="Bad response"
        title="Bad response"
      >
        <Icon name="thumb_down" />
      </button>

      {/* Only on the most recent reply. Regenerating something further back
          would leave the conversation with two answers to one question and no
          indication of which the assistant then reasoned from. */}
      {canRegenerate && (
        <button
          type="button"
          className="ag-msg-action"
          onClick={onRegenerate}
          aria-label="Regenerate response"
          title="Try again"
        >
          <Icon name="refresh" />
        </button>
      )}

      {rateFailed && (
        <span className="ag-msg-action-note" role="status">Couldn’t save that</span>
      )}

      {/* Short form here, full date and time on hover. A conversation needs a
          sense of when things happened; it does not need a datestamp competing
          with every answer for attention. <time> so it is machine-readable and
          a screen reader announces it as a time rather than a bare number. */}
      {message.createdAt && (
        <time
          className="ag-msg-time"
          dateTime={new Date(message.createdAt).toISOString()}
          title={exactTime(message.createdAt)}
        >
          {relativeTime(message.createdAt)}
        </time>
      )}
    </div>
  );
};

export default MessageActions;
